import { useLazyQuery } from '@apollo/client';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubCircularProgress,
  DevHubCollapsibleBox,
  DevHubDynamicIcon,
  DevHubFilter,
  DevHubGrid,
  DevHubList,
  DevHubListItem,
  DevHubListItemText,
  DevHubPopover,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

import {
  Dispatch,
  Fragment,
  MouseEvent,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { AppList } from './AppList';
import { AppTiles } from './AppTiles';
import { ViewToggleButton } from './ViewToggle';
import { StarredViewToggle } from './StarredViewToggle';
import AppSearch from './AppSearch';
import { MyApp } from './types/myAppTypes';
import DevHubSupportButton from '../common/DevHubSupportButton';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { UserContext } from '../../contexts/UserContext';
import {
  useFunctionalAreaContext,
  DATA_LEAD_MANAGEMENT_AD_GROUP,
} from '../../contexts/FunctionalAreaContext';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import {
  CorporateEntity,
  getCorporateEntityFromEmail,
} from '../onboarding/types/onboardingTypes';
import AppEntityIcon from '../common/AppEntityIcon';
import { useSelectedAppContext } from '../../contexts/SelectedAppContext';
import { useNavigate } from 'react-router-dom';
import { UserOwnedAppsQuery } from './UserOwnedAppsQuery';
import { CreateAISystemModal } from '../ai-system/CreateAISystemModal';

enum ViewMode {
  List = 'list',
  Tile = 'tile',
}

/**
 * This component is used to display the applications that a user owns
 * @returns The main view of the MyApps component
 */
export const MyApps = () => {
  const storedViewMode = localStorage.getItem(
    'myAppsViewMode',
  ) as ViewMode | null;
  const [viewMode, setViewMode] = useState<ViewMode>(
    storedViewMode || ViewMode.List,
  );
  const storedStatuses = JSON.parse(
    localStorage.getItem('selectedStatuses') || '["Active", "Pre-Prod"]',
  );

  const [starMode, setStarMode] = useState<boolean>(false);
  const [myApps, setMyApps] = useState<MyApp[]>([]);
  const [searchResults, setSearchResults] = useState<MyApp[]>([]);
  const [filteredApps, setFilteredApps] = useState<MyApp[]>([]);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [checkedFilteredApps, setCheckedFilteredApps] = useState<
    Map<MyApp, boolean>
  >(new Map());
  const { selectedApps, addMultipleApps } = useSelectedAppContext();
  const [allStarredAppsIds, setAllStarredAppIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] =
    useState<string[]>(storedStatuses);
  const [corpEntities, setCorpEntity] = useState<string[]>([]);
  const { login } = useContext(UserContext);
  const { canManageDataLeads } = useFunctionalAreaContext();

  const isCorpEntityEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.CORPORATE_ENTITY,
  );
  const isAiInventoryEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.AI_INVENTORY,
  );
  const includeSystemTypes = useMemo(
    () => (isAiInventoryEnabled ? ['App', 'AiSystem'] : ['App']),
    [isAiInventoryEnabled],
  );
  const [bulkEditApplications, setBulkEditApplications] = useState<MyApp[]>([]);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [loadingApps, setLoadingApps] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState(true);
  const [errorFetchMore, setErrorFetchMore] = useState(false);

  const currentPageRef = useRef(1);
  const observer = useRef<IntersectionObserver | null>(null);
  const INCREMENTAL_COUNT = 50;

  useEffect(() => {
    if (!isCorpEntityEnabled) {
      setCorpEntity([CorporateEntity.CABLE]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCorpEntityEnabled]);

  useEffect(() => {
    localStorage.setItem('selectedStatuses', JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  // Reset edit mode when component mounts - user should explicitly click "Edit Applications" to enter edit mode
  useEffect(() => {
    setIsEditable(false);
  }, []);

  const [fetchApplications, { data, loading, error, fetchMore }] = useLazyQuery(
    UserOwnedAppsQuery,
    {
      errorPolicy: 'all',
      variables: {
        login: login,
        filter: {
          additionalApplicationIds: allStarredAppsIds,
          havingRoles: ['owner', 'assistant_owner', 'approver'],
          includeSystemTypes: includeSystemTypes,
        },
        pagination: {
          offset: 0,
          limit: INCREMENTAL_COUNT,
        },
        sortBy: {
          direction: 'Asc',
          field: 'Name',
        },
      },
    },
  );
  const loadMoreApplications = useCallback(
    async (pageNumber: number) => {
      setLoadingApps(true);

      const offset = (pageNumber - 1) * INCREMENTAL_COUNT;

      const variable = {
        login,
        filter: {
          havingRoles: ['owner', 'assistant_owner', 'approver'],
          includeSystemTypes: includeSystemTypes,
        },
        pagination: {
          offset: offset, // Fetches 50 more apps whenever the user reaches the end of the app list
          limit: INCREMENTAL_COUNT,
        },
        sortBy: {
          direction: 'Asc',
          field: 'Name',
        },
      };

      try {
        // Capture the new page's items via closure so we can check whether
        // the page was empty or partial — fetchMore returns the cumulative
        // merged result (via updateQuery), not just the new page, so we
        // cannot rely on the returned data length to detect end-of-results.
        let newPageApps: MyApp[] = [];

        await fetchMore({
          variables: variable,
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            newPageApps =
              fetchMoreResult.identity.user.resources.applicationsPaged
                .applications ?? [];
            return {
              ...prev,
              identity: {
                ...prev?.identity,
                user: {
                  ...prev?.identity?.user,
                  resources: {
                    applicationsPaged: {
                      applications: [
                        ...(prev?.identity?.user?.resources?.applicationsPaged
                          ?.applications ?? []),
                        ...newPageApps,
                      ],
                      totalCount:
                        fetchMoreResult.identity.user.resources
                          .applicationsPaged.totalCount,
                    },
                  },
                },
              },
            };
          },
        });

        if (newPageApps.length === 0) {
          setHasMore(false);
          setLoadingApps(false);
          return;
        }

        if (newPageApps.length < INCREMENTAL_COUNT) {
          setHasMore(false);
        }

        setMyApps(prevApps => {
          const existingAppIds = new Set(
            prevApps.map(app => app.applicationId),
          );
          const uniqueNewApps = newPageApps.filter(
            app => !existingAppIds.has(app.applicationId),
          );

          return [...prevApps, ...uniqueNewApps];
        });

        setLoadingApps(false);
      } catch (err) {
        setErrorFetchMore(true);
        setHasMore(false);
        setLoadingApps(false);
      }
    },
    [fetchMore, login, includeSystemTypes],
  );

  const lastPostElementRef = useCallback(
    node => {
      if (loading || !hasMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          currentPageRef.current += 1;

          loadMoreApplications(currentPageRef.current);
        }
      });

      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, hasMore],
  );

  const getStarredAppIDs = () => {
    const currentArray = localStorage.getItem('starredApplications');
    const starredAppIds = currentArray ? JSON.parse(currentArray) : [];
    setAllStarredAppIds(starredAppIds);
  };

  const getHighestRole = (roleName: string[] | undefined): string => {
    if (roleName) {
      if (roleName.includes('Owner')) {
        return 'Owner';
      } else if (roleName.includes('Owner Assistant')) {
        return 'Owner Assistant';
      } else if (roleName.includes('iTRC Approver')) {
        return 'iTRC Approver';
      }
    }
    return '';
  };

  const combineApps = (): MyApp[] => {
    const myApplications: MyApp[] = [];
    const processedAppIds: Set<number> = new Set();

    data?.identity?.user?.resources?.applicationsPaged?.applications?.forEach(
      (app: MyApp) => {
        const appId = app?.applicationId;
        if (!appId || processedAppIds.has(appId)) {
          return;
        }

        const starred = allStarredAppsIds?.includes(appId);
        const userRoles = app?.roles?.map(role => role.displayName) || [];
        const allRoles: string[] = [];

        userRoles.forEach(roleName => {
          if (roleName) {
            allRoles.push(roleName);
          }
        });

        const newApp: MyApp = {
          ...app,
          isStarred: starred ?? false,
          role: getHighestRole(allRoles),
          url: `/catalog/default/system/${appId}`,
          functionalArea: app?.functionalAreaConnection?.[0]?.name || '',
        };
        myApplications.push(newApp);
        processedAppIds.add(appId);
      },
    );
    return myApplications;
  };

  const filterApps = () => {
    const apps = searchResults?.length > 0 ? searchResults : myApps;
    const sortedApps = apps.sort((a, b) => a.name.localeCompare(b.name));

    let appsFiltered = sortedApps;

    if (starMode && selectedStatuses.length > 0) {
      appsFiltered = sortedApps.filter(
        app => app.isStarred && selectedStatuses.includes(app.status),
      );
    } else if (starMode) {
      appsFiltered = sortedApps.filter(app => app.isStarred);
    } else if (selectedStatuses.length > 0) {
      appsFiltered = sortedApps.filter(app =>
        selectedStatuses.includes(app.status),
      );
    }

    if (corpEntities.length > 0) {
      appsFiltered = appsFiltered.filter(app =>
        corpEntities.includes(app.corporateEntity),
      );
    }

    setFilteredApps(appsFiltered);
    if (isEditable && selectedApps.length > 0) {
      const selectedAppIds = selectedApps.map(app => app.applicationId);
      setCheckedFilteredApps(
        new Map(
          appsFiltered.map(app => [
            app,
            selectedAppIds.includes(app.applicationId),
          ]),
        ),
      );
    } else {
      setCheckedFilteredApps(new Map(appsFiltered.map(app => [app, false])));
    }

    setLoadingApps(false);
  };

  const navigate = useNavigate();

  const handleBulkEdit = () => {
    // TODO: redirect to edit page
    addMultipleApps(bulkEditApplications);
    navigate('/my-apps/bulk-edit');
    return bulkEditApplications;
  };

  // Actions dropdown handlers
  const handleActionsClick = (event: MouseEvent<HTMLButtonElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const actionsOpen = Boolean(actionsAnchorEl);

  const [aiSystemModalOpen, setAiSystemModalOpen] = useState<boolean>(false);

  const handleActionSelect = (action: string) => {
    handleActionsClose();
    switch (action) {
      case 'edit':
        setIsEditable(true);
        break;
      case 'manage-data-leads':
        navigate('/my-apps/manage-data-leads');
        break;
      case 'create':
        window.open('/onboarding/create', '_self');
        break;
      case 'create-ai-system':
        setAiSystemModalOpen(true);
        break;
      default:
        // No action for unknown cases
        break;
    }
  };

  const handleSelectChange = (app: MyApp) => {
    const checked = checkedFilteredApps.get(app);
    if (checked !== undefined) {
      setCheckedFilteredApps(new Map(checkedFilteredApps.set(app, !checked)));
    }
  };

  useEffect(() => {
    setBulkEditApplications(
      [...checkedFilteredApps.keys()].filter(
        app => checkedFilteredApps.get(app) === true,
      ),
    );
  }, [checkedFilteredApps]);

  const handleItemChange = <T,>(
    item: T | T[],
    selectedItems: T[],
    setSelectedItems: Dispatch<SetStateAction<T[]>>,
  ) => {
    if (Array.isArray(item)) {
      setSelectedItems(item);
    } else {
      const isSelected = selectedItems.includes(item);

      if (isSelected) {
        setSelectedItems(selectedItems.filter(i => i !== item));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    }
  };

  const handleStatusChange = (status: string | string[]) => {
    handleItemChange(status, selectedStatuses, setSelectedStatuses);
  };
  const handleCorpEntityChange = (newCorpEntity: string | string[]) => {
    handleItemChange(newCorpEntity, corpEntities, setCorpEntity);
    filterApps();
  };

  const handleStatusDelete = (statusToDelete: string) => {
    const updatedStatuses = selectedStatuses.filter(
      status => status !== statusToDelete,
    );
    setSelectedStatuses(updatedStatuses);
  };

  const handleCorpEntityDelete = (entityToDelete: string) => {
    const updatedEntities = corpEntities.filter(
      entity => entity !== entityToDelete,
    );
    setCorpEntity(updatedEntities);
  };

  // Fetch applications and starred IDs once when the user's login is known.
  // Kept separate from the data effect so that fetchApplications() is not
  // re-called on every fetchMore cache update (which would risk a re-render loop).
  useEffect(() => {
    getStarredAppIDs();
    if (login) {
      fetchApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  // Populate myApps from the Apollo cache on the initial page load only.
  // Pages 2+ are accumulated directly by loadMoreApplications via setMyApps,
  // so running combineApps() again when data changes due to fetchMore would
  // overwrite the accumulated list with only page-1 data.
  useEffect(() => {
    if (data && corpEntities.length === 0) {
      const entity = getCorporateEntityFromEmail(data?.identity?.user?.email);
      const entities =
        entity && entity !== CorporateEntity.CABLE ? [entity] : [];
      setCorpEntity(entities);
    }
    if (data && currentPageRef.current === 1) {
      const apps = combineApps();
      setMyApps(apps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    filterApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, starMode, myApps, selectedStatuses]);

  const handleViewChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
  };

  const handleStarChange = (newStarMode: boolean) => {
    setStarMode(newStarMode);
  };

  const handleSearchResultsToggle = (appId: number, isStarred: boolean) => {
    const updatedSearchResults = searchResults.map(app => {
      if (app.applicationId === appId) {
        const appPreviouslyAdded = myApps.some(a => a.applicationId === appId);
        if (appPreviouslyAdded) {
          setMyApps(
            myApps.map(oldApp => {
              if (oldApp.applicationId === appId) {
                return { ...oldApp, isStarred };
              }
              return oldApp;
            }),
          );
        } else {
          setMyApps([...myApps, { ...app, isStarred }]);
        }
        return { ...app, isStarred };
      }
      return app;
    });
    setSearchResults(updatedSearchResults);
  };

  const handleStarToggle = (appId: number, isStarred: boolean) => {
    if (searchResults.length > 0) {
      handleSearchResultsToggle(appId, isStarred);
    } else {
      const updatedApps = myApps.map(app => {
        if (app.applicationId === appId) {
          return { ...app, isStarred };
        }
        return app;
      });
      setMyApps(updatedApps);
    }
  };

  return (
    <Page themeId="home">
      <Header
        title="Applications"
        subtitle="Applications you have a certain role on, or have starred."
      >
        <DevHubBox display="flex" justifyContent="flex-end">
          <DevHubSupportButton color="white">Support</DevHubSupportButton>
        </DevHubBox>
      </Header>
      <Content>
        <DevHubBox display="flex" justifyContent="space-between">
          <DevHubGrid container item spacing={1} xs={8}>
            <DevHubGrid item>
              <ViewToggleButton
                onViewChange={handleViewChange}
                viewMode={viewMode}
              />
            </DevHubGrid>
            <DevHubGrid item>
              <StarredViewToggle
                onViewChange={handleStarChange}
                starMode={starMode}
              />
            </DevHubGrid>
            <ConfigFeatureCheck feature={Feature.CORPORATE_ENTITY}>
              <DevHubGrid item>
                <DevHubFilter
                  selectedItems={corpEntities}
                  list={[
                    CorporateEntity.CABLE,
                    CorporateEntity.NBCU,
                    CorporateEntity.SKY,
                  ]}
                  title="Corporate Entity"
                  height="medium"
                  handleChange={handleCorpEntityChange}
                  useStatusColor={false}
                />
              </DevHubGrid>
            </ConfigFeatureCheck>
            <DevHubGrid item>
              <DevHubFilter
                selectedItems={selectedStatuses}
                list={['Active', 'Pre-Prod', 'Retired']}
                title={isCorpEntityEnabled ? 'Status' : 'Filter By'}
                height="medium"
                handleChange={handleStatusChange}
                useStatusColor
              />
            </DevHubGrid>
          </DevHubGrid>
          <DevHubGrid
            container
            item
            spacing={1}
            xs={4}
            justifyContent="flex-end"
          >
            {isEditable && (
              <ConfigFeatureCheck feature={Feature.APP_BULK_EDIT}>
                <DevHubGrid container spacing={1}>
                  <DevHubGrid item>
                    <DevHubButton
                      variant="outlined"
                      color="primary"
                      size="large"
                      onClick={() => {
                        setIsEditable(false);
                        setCheckedFilteredApps(
                          new Map(
                            [...checkedFilteredApps].map(([key]) => [
                              key,
                              false,
                            ]),
                          ),
                        );
                      }}
                    >
                      Cancel
                    </DevHubButton>
                  </DevHubGrid>
                  <DevHubGrid item>
                    <DevHubButton
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleBulkEdit}
                    >
                      Proceed to edit
                    </DevHubButton>
                  </DevHubGrid>
                </DevHubGrid>
              </ConfigFeatureCheck>
            )}
            {!isEditable && (
              <DevHubGrid item>
                <DevHubButton
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleActionsClick}
                  aria-describedby={actionsOpen ? 'actions-popover' : undefined}
                >
                  Actions
                  <DevHubDynamicIcon iconName="ArrowDropDown" />
                </DevHubButton>
                <DevHubPopover
                  id="actions-popover"
                  open={actionsOpen}
                  anchorEl={actionsAnchorEl}
                  onClose={handleActionsClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <DevHubBox p={1}>
                    <DevHubList>
                      <ConfigFeatureCheck feature={Feature.APP_BULK_EDIT}>
                        <DevHubListItem
                          button
                          onClick={() => handleActionSelect('edit')}
                        >
                          <DevHubBox pr={1}>
                            <DevHubDynamicIcon iconName="Edit" />
                          </DevHubBox>
                          <DevHubListItemText primary="Edit Applications" />
                        </DevHubListItem>
                      </ConfigFeatureCheck>
                      <DevHubListItem
                        button
                        onClick={() => handleActionSelect('create')}
                      >
                        <DevHubBox pr={1}>
                          <DevHubDynamicIcon iconName="Add" />
                        </DevHubBox>
                        <DevHubListItemText primary="Create an Application" />
                      </DevHubListItem>
                      <ConfigFeatureCheck feature={Feature.AI_INVENTORY}>
                        <DevHubListItem
                          button
                          onClick={() => handleActionSelect('create-ai-system')}
                        >
                          <DevHubBox pr={1}>
                            <DevHubDynamicIcon iconName={'Memory' as any} />
                          </DevHubBox>
                          <DevHubListItemText primary="Create an AI System" />
                        </DevHubListItem>
                      </ConfigFeatureCheck>
                      <ConfigFeatureCheck feature={Feature.DBO_MANAGEMENT}>
                        <DevHubTooltip
                          title={`Data leads can be managed by functional area data business owners, data leads, DevHub admins, or members of the ${DATA_LEAD_MANAGEMENT_AD_GROUP} Active Directory group`}
                          placement="left"
                        >
                          <span>
                            <DevHubListItem
                              button
                              disabled={!canManageDataLeads}
                              onClick={() =>
                                canManageDataLeads &&
                                handleActionSelect('manage-data-leads')
                              }
                            >
                              <DevHubBox pr={1}>
                                <DevHubDynamicIcon iconName="People" />
                              </DevHubBox>
                              <DevHubListItemText primary="Manage Data Leads" />
                            </DevHubListItem>
                          </span>
                        </DevHubTooltip>
                      </ConfigFeatureCheck>
                    </DevHubList>
                  </DevHubBox>
                </DevHubPopover>
              </DevHubGrid>
            )}
          </DevHubGrid>
        </DevHubBox>
        <ConfigFeatureCheck feature={Feature.CORPORATE_ENTITY}>
          {(selectedStatuses.length > 0 || corpEntities.length > 0) && (
            <DevHubCollapsibleBox
              title="Filters"
              showBadge
              badgeContent={selectedStatuses.length + corpEntities.length}
            >
              {selectedStatuses.length > 0 && (
                <DevHubGrid container alignItems="center" margin="small">
                  <DevHubTypography>Status:</DevHubTypography>
                  {selectedStatuses.map((status: string, index: number) => (
                    <DevHubGrid key={index}>
                      <DevHubChip
                        label={status}
                        onDelete={() => handleStatusDelete(status)}
                      />
                    </DevHubGrid>
                  ))}
                </DevHubGrid>
              )}
              {corpEntities.length > 0 && (
                <DevHubGrid container alignItems="center" margin="small">
                  <DevHubTypography>Corporate Entities: </DevHubTypography>
                  {corpEntities.map((entity, index) => (
                    <DevHubGrid centerContent key={index}>
                      <DevHubChip
                        size="medium"
                        icon={<AppEntityIcon entity={entity} size="small" />}
                        onDelete={() => handleCorpEntityDelete(entity)}
                      />
                    </DevHubGrid>
                  ))}
                </DevHubGrid>
              )}
            </DevHubCollapsibleBox>
          )}
        </ConfigFeatureCheck>
        {!isEditable && (
          <DevHubGrid padding="medium">
            <AppSearch
              setSearchResults={setSearchResults}
              searchResults={searchResults}
              starMode={starMode}
              selectedStatuses={selectedStatuses}
              corpEntities={corpEntities}
            />
          </DevHubGrid>
        )}
        {isEditable && (
          <DevHubGrid
            tbPadding="medium"
            style={{ position: 'sticky', top: '0', zIndex: '10' }}
          >
            <DevHubAlert severity="warning">
              You've selected {bulkEditApplications.length} apps (max limit 25).
            </DevHubAlert>
          </DevHubGrid>
        )}
        {loading && <Progress />}
        {!loading &&
          error &&
          (myApps?.length === 0 ? (
            <DevHubAlert severity="info">
              No applications found. You may not have an assigned role on an
              application or you do not have any starred apps to display.
            </DevHubAlert>
          ) : (
            <DevHubAlert severity="error">
              Error loading applications
            </DevHubAlert>
          ))}
        {errorFetchMore && (
          <DevHubAlert severity="error">
            Error encountered while fetching multiple applications
          </DevHubAlert>
        )}
        {!loading && !error && (
          // List View Mode that checks for filters
          <Fragment>
            {viewMode === 'list' ? (
              <DevHubGrid container>
                {filteredApps.map((app: MyApp, index: number) => {
                  const canLoad = index === filteredApps.length - 1;

                  let ref;
                  if (canLoad) {
                    ref = lastPostElementRef;
                  }
                  return (
                    ((isEditable &&
                      (app?.role === 'Owner' ||
                        app?.role === 'iTRC Approver' ||
                        app?.role === 'Owner Assistant')) ||
                      !isEditable) && (
                      <DevHubGrid key={index} item xs={12}>
                        <div ref={ref}>
                          <AppList
                            link={app.url}
                            app={app}
                            onStarChange={handleStarToggle}
                            isEditable={isEditable}
                            checked={checkedFilteredApps.get(app)}
                            changeSelect={handleSelectChange}
                            disabled={bulkEditApplications.length >= 25}
                          />
                        </div>
                      </DevHubGrid>
                    )
                  );
                })}
              </DevHubGrid>
            ) : (
              // Tab View Mode that checks for filters
              <DevHubGrid container>
                {filteredApps.map((app: MyApp, index: number) => {
                  const canLoad = index === filteredApps.length - 1;

                  let ref;
                  if (canLoad) {
                    ref = lastPostElementRef;
                  }

                  return (
                    ((isEditable &&
                      (app?.role === 'Owner' ||
                        app?.role === 'iTRC Approver' ||
                        app?.role === 'Owner Assistant')) ||
                      !isEditable) && (
                      <DevHubGrid key={index} item xs={12} md={6} lg={4}>
                        <div ref={ref}>
                          <AppTiles
                            link={app.url}
                            app={app}
                            onStarChange={handleStarToggle}
                            isEditable={isEditable}
                            checked={checkedFilteredApps.get(app)}
                            changeSelect={handleSelectChange}
                            disabled={bulkEditApplications.length >= 25}
                          />
                        </div>
                      </DevHubGrid>
                    )
                  );
                })}
              </DevHubGrid>
            )}

            <DevHubBox display="flex" justifyContent="center" m={5}>
              {loadingApps && <DevHubCircularProgress />}
            </DevHubBox>
          </Fragment>
        )}

        {!loading && !error && filteredApps.length === 0 && (
          <DevHubAlert severity="info">
            You do not have any starred apps to display. Select the 'View All
            Apps' button above to view all applications.
          </DevHubAlert>
        )}
      </Content>
      {aiSystemModalOpen && (
        <CreateAISystemModal
          open={aiSystemModalOpen}
          onClose={() => setAiSystemModalOpen(false)}
        />
      )}
    </Page>
  );
};
